import React, { useEffect } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';

import { Box, Checkbox, useTheme } from '@mui/material';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import LoadingSpinner from 'components/loading-spinner';
import NewListRow from 'components/new-list-row';
import Tooltip from 'components/tooltip';
import ScenarioFeedsTooltip from 'views/bluviu-nutrition/components/scenario-feeds-tooltip';
import ViewGraph from 'views/bluviu-nutrition/features/scenarios/components/view-results/results/view-graph';
import { ScenarioResult, Summary } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';

import Paper from 'components/paper';
import ScenarioResultRowBody from 'views/bluviu-nutrition/features/scenarios/components/view-results/results/scenario-result-row-body';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-result-context';
import { ReactComponent as BestBiologyIcon } from '../../../assets/best-biology-plan-icon.svg';
import { ReactComponent as BestEconomyIcon } from '../../../assets/best-economy-plan-icon.svg';
import { ReactComponent as BestEnvironmentIcon } from '../../../assets/best-environment-plan-icon.svg';

type Props = {
  renderTopPerformers: () => JSX.Element;
  showCheckbox: boolean;
  summaryResult: ScenarioResult | undefined;
  setShowCompareBottomBar: (value: React.SetStateAction<boolean>) => void;
};

const ScenarioFeedList: React.FC<Props> = ({ renderTopPerformers, setShowCompareBottomBar, showCheckbox, summaryResult }) => {
  const theme = useTheme();
  const intl = useIntl();
  const PAGE_NAME_ID = 'bluViuNutrition.viewScenarioResultGt2';
  const {
    isLoading,
    setSummaries,
    summaries,
    feedPlans,
    showGraph,
    selectedFeedPlans,
    selectedGraphData,
    setSelectedFeedPlans,
    setSelectedGraphData,
  } = useScenarioResultState();

  const listOptions: HeadCell[] = [
    { id: 'feedPlan', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.feedPlan` }) },
    { id: 'icon', align: 'left', label: '' },
    {
      id: 'finalWeightGrams',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.finalWeightGrams` }),
      info: intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.finalWeightGrams` }),
    },
    {
      id: 'bFcr',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.bFcr` }),
      info: intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.bFcr` }),
    },

    {
      id: 'averageFeedCost',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.averageFeedCost` }),
      info: intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.averageFeedCost` }),
    },
    {
      id: 'feedCostOfHogGrowth',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.feedCostOfHogGrowth` }),
      info: intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.feedCostOfHogGrowth` }),
    },
    {
      id: 'globalWarmingPotentialCo2KgPerKgGrowth',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.globalWarmingPotentialCo2KgPerKgGrowth` }),
      info: intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.globalWarmingPotentialCo2KgPerKgGrowth` }),
    },
  ];

  useEffect(() => {
    setShowCompareBottomBar(selectedFeedPlans.length > 1);
  }, [selectedFeedPlans]);

  const getSelectedFeedIcon = (feed: Summary) => {
    return (
      <>
        {feed.feedPlan === feedPlans.lowestCo2Plan.name && <BestEnvironmentIcon />}
        {feed.feedPlan === feedPlans.bestBiologyPlan.name && <BestBiologyIcon />}
        {feed.feedPlan === feedPlans.mostEconomicalPlan.name && <BestEconomyIcon />}
      </>
    );
  };

  const handleSelectedFeeds = (feed: Summary, checked: boolean) => {
    if (selectedFeedPlans.length <= 4) {
      const filterGraph = summaryResult?.graphData.filter((s) => s.feedPlan === feed.feedPlan);
      feed.icon = getSelectedFeedIcon(feed);

      setSelectedGraphData(
        checked ? selectedGraphData.concat(filterGraph ?? []) : selectedGraphData.filter((s) => s.feedPlan != feed.feedPlan)
      );
      setSelectedFeedPlans(checked ? selectedFeedPlans.concat(feed) : selectedFeedPlans.filter((s) => s != feed));
    }
  };

  const iconHandler = (summary: Summary) => {
    const { lowestCo2Plan, bestBiologyPlan, mostEconomicalPlan } = feedPlans;
    const icons: JSX.Element[] = [];
    if (summary.feedPlan === lowestCo2Plan.name) {
      icons.push(
        <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.icons.lowestCo2Title` })}>
          <span>
            <BestEnvironmentIcon />
          </span>
        </Tooltip>
      );
    }
    if (summary.feedPlan === bestBiologyPlan.name) {
      icons.push(
        <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.icons.bestBioTitle` })}>
          <span>
            <BestBiologyIcon />
          </span>
        </Tooltip>
      );
    }
    if (summary.feedPlan === mostEconomicalPlan.name) {
      icons.push(
        <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.icons.mostEconomicalTitle` })}>
          <span>
            <BestEconomyIcon />
          </span>
        </Tooltip>
      );
    }
    return icons;
  };

  return (
    <Paper transparent>
      {renderTopPerformers()}
      {isLoading ? (
        <LoadingSpinner />
      ) : !showGraph ? (
        <Box>
          {summaries && (
            <List orderBy={'feedPlan'} options={listOptions} rows={summaries} sort={setSummaries}>
              {summaries.map((item, idx) => (
                <NewListRow colSpan={8} key={`${item.feedPlan}_${idx}`} body={<ScenarioResultRowBody summary={item} />}>
                  <>
                    <ListCell align={'left'}>
                      <Box display={'flex'} alignItems={'center'}>
                        {showCheckbox && (
                          <Checkbox
                            disabled={selectedFeedPlans.length >= 5 && !selectedFeedPlans.includes(item)}
                            checked={selectedFeedPlans.includes(item)}
                            onChange={(_, checked) => handleSelectedFeeds(item, checked)}
                          />
                        )}
                        <Box overflow="hidden" textOverflow="ellipsis" maxWidth={500}>
                          <ScenarioFeedsTooltip feedPlan={item.feedPlan} feedSummary={item.feeds} display="both" />
                        </Box>
                      </Box>
                    </ListCell>
                    <ListCell align={'left'}>
                      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                        {iconHandler(item).map((icon) => icon)}
                      </Box>
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber value={Number(item.finalWeightGrams)} maximumFractionDigits={0} style={`unit`} unit="gram" />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber value={Number(item.bFcr)} maximumFractionDigits={2} />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber value={Number(item.averageFeedCost)} maximumFractionDigits={2} />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber value={Number(item.feedCostOfHogGrowth)} maximumFractionDigits={2} />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber
                        value={Number(item.globalWarmingPotentialCo2KgPerKgGrowth)}
                        maximumFractionDigits={2}
                        style={`unit`}
                        unit="kilogram"
                      />
                    </ListCell>
                  </>
                </NewListRow>
              ))}
            </List>
          )}
        </Box>
      ) : (
        <ViewGraph graphData={summaryResult?.graphData} />
      )}
    </Paper>
  );
};

export default ScenarioFeedList;
